import { Button, Progress, Text, VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { taskService, type TaskSummary } from '@/lib/services/taskService'
import OverviewSectionCard from '@/components/helpdesk/TicketDetail/overview/OverviewSectionCard'
import { computeTaskStats, isTaskComplete } from './taskUtils'

type Props = {
  tasks: ChildTicketTask[]
  parentTicketId?: string | null
  onViewTasks?: () => void
}

export default function WorkProgressWidget({ tasks, parentTicketId, onViewTasks }: Props) {
  const colors = useThemeColors()
  const [taskSummary, setTaskSummary] = useState<TaskSummary | null>(null)

  useEffect(() => {
    if (!parentTicketId) {
      setTaskSummary(null)
      return
    }

    let cancelled = false
    taskService
      .getTaskSummary(parentTicketId)
      .then((summary) => {
        if (!cancelled) setTaskSummary(summary)
      })
      .catch(() => {
        if (!cancelled) setTaskSummary(null)
      })

    return () => {
      cancelled = true
    }
  }, [parentTicketId, tasks.length])

  const localStats = useMemo(() => computeTaskStats(tasks), [tasks])
  const stats = useMemo(() => {
    if (!taskSummary || taskSummary.total === 0) return localStats
    return {
      ...localStats,
      total: taskSummary.total,
      completed: taskSummary.completed_count,
      open: taskSummary.open_count,
      percent:
        taskSummary.total > 0
          ? Math.round((taskSummary.completed_count / taskSummary.total) * 100)
          : 0,
    }
  }, [localStats, taskSummary])

  const nextOpenTask = useMemo(
    () => tasks.find((t) => !isTaskComplete(t)),
    [tasks],
  )

  if (stats.total === 0) {
    return (
      <OverviewSectionCard
        title="Work progress"
        rightAction={
          onViewTasks ? (
            <Button size="xs" variant="ghost" fontWeight="normal" px={2} onClick={onViewTasks}>
              View tasks
            </Button>
          ) : undefined
        }
      >
        <Text fontSize="sm" color={colors.textSecondary}>
          No tasks yet.
        </Text>
      </OverviewSectionCard>
    )
  }

  const statusParts: string[] = []
  if (stats.breached > 0) statusParts.push(`${stats.breached} breached`)
  if (stats.bottleneckTeam) statusParts.push(`bottleneck: ${stats.bottleneckTeam}`)
  else if (stats.dueSoon > 0) statusParts.push(`${stats.dueSoon} due soon`)
  if (stats.skipped > 0) statusParts.push(`${stats.skipped} skipped`)

  return (
    <OverviewSectionCard
      title="Work progress"
      rightAction={
        onViewTasks ? (
          <Button size="xs" variant="ghost" fontWeight="normal" px={2} onClick={onViewTasks}>
            View tasks
          </Button>
        ) : undefined
      }
    >
      <VStack align="stretch" spacing={1.5}>
        <Text fontSize="sm" fontWeight="500" color={colors.text}>
          {stats.completed} of {stats.total} complete
        </Text>
        <Progress value={stats.percent} size="xs" colorScheme="blue" borderRadius="full" h="5px" />
        {statusParts.length > 0 ? (
          <Text fontSize="11px" color={stats.breached > 0 ? 'red.600' : colors.textSecondary}>
            {statusParts.join(' · ')}
          </Text>
        ) : (
          <Text fontSize="11px" color={colors.textMuted}>
            All open tasks on track
          </Text>
        )}
        {nextOpenTask?.title ? (
          <Text fontSize="11px" color={colors.textSecondary} noOfLines={1}>
            Next: {nextOpenTask.title}
          </Text>
        ) : null}
      </VStack>
    </OverviewSectionCard>
  )
}
